import { Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { DirtyDialogComponent } from 'src/app/core/components/dirty-dialog.component';
import { GroupTypeDiscriminator, ParticipantTransferModel } from 'src/app/core/services/api-services';
import { MessageService } from 'src/app/core/services/message.service';
import { ParticipantTransferService, SessionDateService, VisitDateService, VisitGroupService, SessionGroupService, VisitService, SessionService } from '../../../core/services/api-services';
import { DetailsEditDialogComponent } from '../../session/shared/presentationals/edit-dialogs/details-edit-dialog/details-edit-dialog.component';

@Component({
    selector: 'app-shared-transfer-participants',
    templateUrl: './transfer-participants.component.html',
    styleUrls: ['./transfer-participants.component.scss'],
    standalone: false
})
export class TransferParticipantsDialogComponent extends DirtyDialogComponent implements OnInit {

  allSelected: boolean = false;

  sessionOrVisitId: string;
  dateId: string;
  groupId: string | undefined;
  type: GroupTypeDiscriminator;

  currentDate: any;
  targetDate: any;
  dates: any[] = [];
  groups: any[] = [];

  currentDateTitle: string = "";

  groupIds: string[] = [];

  numberOfRegistrations: number = 0;

  types = GroupTypeDiscriminator;

  formGroup = new FormGroup({
    participantIds: new FormControl<any[] | null>(null, { validators: [Validators.required] }),
    targetDateId: new FormControl<string | null>(null, { validators: [Validators.required] }),
    selectAll: new FormControl<boolean>(false)
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) data: { sessionOrVisitId: string, dateId: string, type: GroupTypeDiscriminator, groupId: string | undefined },
    protected override readonly messageService: MessageService,
    protected override readonly matDialogRef: MatDialogRef<DetailsEditDialogComponent>,
    private readonly transferService: ParticipantTransferService,
    private readonly translateService: TranslateService,
    private readonly visitGroupsService: VisitGroupService,
    private readonly sessionService: SessionService,
    private readonly visitService: VisitService,
    private readonly sessionGroupsService: SessionGroupService,
    @Inject(LOCALE_ID) private readonly locale: string,
    private readonly sessionDatesService: SessionDateService,
    private readonly visitDatesService: VisitDateService,) {
    super(messageService, matDialogRef);
    this.sessionOrVisitId = data.sessionOrVisitId!;
    this.dateId = data.dateId!;
    this.groupId = data.groupId;
    this.type = data.type!;
  }

  override ngOnInit() {

    if (this.type == GroupTypeDiscriminator.Session) {
      this.sessionDatesService.getDatesBySessionId(this.sessionOrVisitId).subscribe(dates => {
        this.sessionService.get(this.sessionOrVisitId).subscribe(session => {
          this.currentDate = dates?.find(d => d.id == this.dateId);
          this.currentDateTitle = session?.sessionTranslations?.find(t => t.languageCode == this.locale.toString())?.title ?? "";
          this.dates = dates!.filter(d => d.id != this.dateId);
        });

        if (this.groupId) {
          this.sessionGroupsService.get(this.groupId).subscribe(group => {
            this.groups.push(group);
            this.participantIdsControl.patchValue([...this.groups]);
          });
        } else {
          this.sessionGroupsService.getBySessionDateId(this.dateId).subscribe(groups => {
            this.groups = groups!;
          });
        }
      });
    } else {
      this.visitDatesService.getDatesByVisitId(this.sessionOrVisitId, null, false, false).subscribe(dates => {
        this.visitService.get(this.sessionOrVisitId).subscribe(session => {
          this.currentDate = dates?.find(d => d.id == this.dateId);
          this.currentDateTitle = session?.visitTranslations?.find(t => t.languageCode == this.locale.toString())?.title ?? "";
          this.dates = dates!.filter(d => d.id != this.dateId);
        });

        if (this.groupId) {
          this.visitGroupsService.get(this.groupId).subscribe(group => {
            this.groups.push(group);
            this.participantIdsControl.patchValue([...this.groups]);
          });
        } else {
          this.visitGroupsService.getByVisitDateId(this.dateId).subscribe(groups => {
            this.groups = groups!;
          });
        }
      });
    }

    this.attachEvents();
  }

  attachEvents(): void {
    this.targetDateIdControl.valueChanges.unsubscribeOnDestroy(this).subscribe(val => {
      this.targetDate = this.dates.find(d => d.id == val);
    });

    this.participantIdsControl.valueChanges.unsubscribeOnDestroy(this).subscribe(val => {
      this.numberOfRegistrations = 0;
      this.groupIds = [];

      val.forEach(element => {
        this.numberOfRegistrations = this.numberOfRegistrations + element.size;
        this.groupIds.push(element.id);

        if (val.length == this.groups.length) {
          this.selectAllControl.setValue(true, { emitEvent: false });
        } else {
          this.selectAllControl.setValue(false, { emitEvent: false });
        }
      });
    });

    this.selectAllControl.valueChanges.unsubscribeOnDestroy(this).subscribe(val => {
      if (val) {
        this.participantIdsControl.patchValue([...this.groups]);
      } else {
        this.participantIdsControl.patchValue([]);
      }
    });
  }

  submit(): void {
    this.formGroup.markAllAsTouched();

    if (this.formGroup.valid) {
      const confirm = this.messageService.openConfirm(this.translateService.instant('Common.Messages.TransferConfirmation', { amount: this.numberOfRegistrations }), this.translateService.instant('Common.Yes'));
      confirm
        .afterDismissed()
        .unsubscribeOnDestroy(this)
        .subscribe(v => {
          if (confirm.instance.isConfirmed) {
            let model: ParticipantTransferModel = {
              groupIds: this.groupIds,
              dateId: this.targetDateIdControl.value,
              type: GroupTypeDiscriminator.Visit
            }

            this.transferService.bulkTransfer(model).subscribe(val => {
              this.messageService.openSaveSuccess();
              this.matDialogRef.close();
            })
          }
        });
    }
  }

  get targetDateIdControl(): FormControl {
    return this.formGroup.controls['targetDateId'];
  }

  get participantIdsControl(): FormControl {
    return this.formGroup.controls['participantIds'];
  }

  get selectAllControl(): FormControl {
    return this.formGroup.controls['selectAll'];
  }
}
